// src/components/partners/tabs/RequestsTabShadcn.jsx
import React, { useState } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';

// Import the shadcn requests management view
import RequestsManagementViewShadcn from '../../ui/lists/RequestsManagementView/RequestsManagementViewShadcn';

import { cn } from '../../../utils/cn';

const RequestsTabShadcn = ({
  pendingConnections = {},
  connectionLoading = false,
  isConnected = true,
  onAcceptConnection,
  onRejectConnection,
  onRefresh,
  className = ''
}) => {
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionError, setActionError] = useState('');

  // Handle request actions
  const handleAcceptRequest = async (requestId, businessName) => {
    try {
      setActionError('');
      setActionSuccess('');

      if (!isConnected) {
        setActionError('You must be online to accept connection requests');
        return;
      }

      console.log('✅ Accept connection request:', requestId, businessName);

      if (onAcceptConnection) {
        await onAcceptConnection(requestId, businessName);
        setActionSuccess(`Connection with ${businessName} accepted! They are now in your partner network.`);
      }

      // Clear success message after 5 seconds
      setTimeout(() => setActionSuccess(''), 5000);
    } catch (error) {
      console.error('Accept request error:', error);
      setActionError(error.message || 'Failed to accept connection request');
    }
  };

  const handleRejectRequest = async (requestId, businessName) => {
    try {
      setActionError('');
      setActionSuccess('');

      if (!isConnected) {
        setActionError('You must be online to reject connection requests');
        return;
      }

      if (window.confirm(`Are you sure you want to decline the connection request from ${businessName}?`)) {
        console.log('❌ Reject connection request:', requestId, businessName);

        if (onRejectConnection) {
          await onRejectConnection(requestId, businessName);
          setActionSuccess(`Connection request from ${businessName} declined.`);
        }

        // Clear success message after 3 seconds
        setTimeout(() => setActionSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Reject request error:', error);
      setActionError(error.message || 'Failed to reject connection request');
    }
  };

  const handleCancelRequest = async (requestId, businessName) => {
    try {
      setActionError('');
      setActionSuccess('');

      if (!isConnected) {
        setActionError('You must be online to cancel connection requests');
        return;
      }

      if (window.confirm(`Are you sure you want to cancel your connection request to ${businessName}?`)) {
        console.log('🚫 Cancel connection request:', requestId, businessName);

        // TODO: Implement cancel request functionality
        // For now, just show a message
        setActionSuccess(`Connection request to ${businessName} cancelled.`);

        // Clear success message after 3 seconds
        setTimeout(() => setActionSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Cancel request error:', error);
      setActionError(error.message || 'Failed to cancel connection request');
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const clearMessages = () => {
    setActionSuccess('');
    setActionError('');
  };

  return (
    <div className={className}>
      {/* Connection Status Warning */}
      {!isConnected && (
        <Alert className="mb-6">
          <WifiOff className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <p className="font-medium">You're currently offline</p>
              <p className="text-sm">Request data may not be up to date. Reconnect to see latest changes.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Success Messages */}
      {actionSuccess && (
        <Alert className="mb-6 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <AlertDescription className="flex items-center justify-between">
            <span>{actionSuccess}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error Messages */}
      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            <span>{actionError}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Requests Management */}
      <RequestsManagementViewShadcn
        pendingConnections={pendingConnections}
        loading={connectionLoading}
        onAcceptRequest={handleAcceptRequest}
        onRejectRequest={handleRejectRequest}
        onCancelRequest={handleCancelRequest}
      />
    </div>
  );
};

export default RequestsTabShadcn;